import React, { ReactNode, useMemo } from 'react';
import { Dropdown, MenuProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';

export interface BreadcrumbDropdownItem {
  key: string | number;
  label: ReactNode;
  /** Called when the item is selected. */
  onClick?: () => void;
  disabled?: boolean;
}

interface BreadcrumbDropdownProps {
  /** Visible label for the trigger (e.g. the current dealer's short name). */
  label: ReactNode;
  /** Items shown in the popup menu. */
  items: BreadcrumbDropdownItem[];
  /** Currently active item — gets the selected style. */
  selectedKey?: string | number;
  /** Disable the trigger entirely (e.g. on detail pages where switching is unsafe). */
  disabled?: boolean;
  /** Tooltip-style title attribute for the trigger. */
  title?: string;
}

/**
 * Inline breadcrumb crumb that opens a dropdown of peer items (e.g. switch
 * companies / sub-sections in place). Renders as a transparent pill with a
 * trailing chevron so it reads as a clickable crumb rather than a form input.
 */
const BreadcrumbDropdown: React.FC<BreadcrumbDropdownProps> = ({
  label,
  items,
  selectedKey,
  disabled,
  title,
}) => {
  const menu: MenuProps = useMemo(
    () => ({
      items: items.map(item => ({
        key: String(item.key),
        label: item.label,
        disabled: item.disabled,
        onClick: () => item.onClick?.(),
      })),
      selectedKeys: selectedKey !== undefined ? [String(selectedKey)] : undefined,
    }),
    [items, selectedKey]
  );

  return (
    <Dropdown
      menu={menu}
      trigger={['click']}
      disabled={disabled || items.length === 0}
      overlayClassName="breadcrumb-dropdown-overlay"
    >
      <span
        className={`breadcrumb-dropdown-trigger${disabled ? ' is-disabled' : ''}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        title={title}
        onClick={e => e.stopPropagation()}
      >
        <span className="breadcrumb-dropdown-label">{label}</span>
        <DownOutlined className="breadcrumb-dropdown-chevron" />
      </span>
    </Dropdown>
  );
};

export default BreadcrumbDropdown;
